import { getS3, getUp, getTxt, getChk, getStore } from './stepcommon';
import { getVers } from './gendocs';
import { mval, coVal } from './collect';
import { kitChecklist } from './kit13';
import { vicsFundDone, vicsChecklist } from './vics13';
import { LAST } from './costep';
import { regCoOf, feeOf, FEE_EXTRAS_SEP } from './coterms';
import type { Project, CoCard, StoreKey } from './types';
import { checkRegDate } from './regdate';
import { feeExtrasFor } from './types';

export interface Readiness { done: number; total: number; missing: string[]; }

function regOk(p: Project): boolean { if (p.done || (p.step || 0) > 7) return true;   // 이미 등록을 마친 조합은 점검하지 않는다
  const r = checkRegDate(p); return r.level !== 'late' && r.level !== 'none' && r.stale.length === 0; }
function regMissLabel(p: Project): string {
  const r = checkRegDate(p);
  if (r.level === 'none') return '결성총회일';
  if (r.level === 'late') return '등록 신청 기한 지남(총회 후 14일)';
  return r.stale.length ? '등록 서류 날짜 맞추기 ' + r.stale.length + '종' : '등록 서류 날짜';
}

function chk(list: [string, boolean][]): Readiness {
  const missing = list.filter((x) => !x[1]).map((x) => x[0]);
  return { done: list.length - missing.length, total: list.length, missing };
}
const sealed = (p: Project, k: string) => getVers(p, k).slice(-1)[0].kind !== 'auto';

/** 법인 1곳의 단계별 체크 항목 — 단계 합산(stepReadiness)과 법인별 진행바(coReadiness)가 공유 */
export function coItems(p: Project, co: CoCard, n: number): [string, boolean][] {
  const c = p.collect;
  if (!c) return [];
  const nm = co.name || '법인';
  const list: [string, boolean][] = [];
  if (n === 8 || n === 9) {
    const store = ('s' + n) as StoreKey;
    const rowDone = (slot: string, mapKey: string, isText?: boolean): boolean => {
      const key = co.id + '_' + slot;
      const st = getStore(p, store);
      const linked = coVal(c, co, mapKey);
      const src = (st.src && st.src[key]) || (linked ? 'link' : 'upload');
      if (src === 'link') return !!linked;
      if (isText) return !!getTxt(p, store, key).trim();
      return !!getUp(p, store, key);
    };
    if (n === 8) {
      list.push([nm + '·사업자등록증', rowDone('biz', 'co_biz')]);
      list.push([nm + '·정관', rowDone('rule', 'co_rule')]);
      list.push([nm + '·등기부등본', rowDone('reg', 'co_reg')]);
      list.push([nm + '·재무제표', rowDone('fin', 'co_fin')]);
      list.push([nm + '·사채 청약서·인수증', !!getUp(p, store, co.id + '_bond')]);
    } else {
      list.push([nm + '·확인시스템 ID·PW', rowDone('idpw', 'co_idpw', true)]);
      list.push([nm + '·중소기업 확인서', rowDone('sme', 'co_sme')]);
      list.push([nm + '·부가세 신고서', rowDone('vat', 'co_vat')]);
      list.push([nm + '·재무제표', rowDone('fin', 'co_fin')]);
      list.push([nm + '·등기부등본(발행 후)', !!getUp(p, store, co.id + '_regcb')]);
    }
  } else if (n === 10) {
    list.push([nm + '·심사비 결제', getChk(p, 's10', co.id)]);
  } else if (n === 11) {
    list.push([nm + '·확인서 송부', getChk(p, 's11send', co.id + '_sent')]);
  } else if (n === 12) {
    list.push([nm + '·확인서 출력', getChk(p, 's11', co.id + '_issued')]);
    list.push([nm + '·확인서 PDF 보관', !!getUp(p, 's11', co.id + '_vcert')]);
  } else if (n === 13) {
    vicsChecklist(p, co).forEach((x) => list.push(x));           // VICS 1313·1319·1320 (법인별) · 1329 는 조합 단위로 stepReadiness 가 1건
  } else if (n === 14) {
    kitChecklist(p, co).forEach((x) => list.push(x));            // 방식 선택 + 방식별 서류 + 방식별 발급 완료 (kit13)
  } else if (n === 15) {
    list.push([nm + '·수수료 청구', getChk(p, 's11', co.id + '_billed')]);
    list.push([nm + '·입금', getChk(p, 's11', co.id + '_paid')]);
    // 별도 청구 부대비용(금액 확정분)은 항목별 청구·입금까지
    FEE_EXTRAS_SEP(feeOf(regCoOf(p, co)), feeExtrasFor(regCoOf(p, co))).forEach(([k, label]) => {
      list.push([nm + '·' + label + ' 청구', getChk(p, 's11', co.id + '_billed_' + k)]);
      list.push([nm + '·' + label + ' 입금', getChk(p, 's11', co.id + '_paid_' + k)]);
    });
  }
  return list;
}

/** 법인 1곳 기준 준비도 — 법인별 진행바에 표시 */
export function coReadiness(p: Project, co: CoCard, n: number): Readiness {
  return chk(coItems(p, co, n));
}

export function stepReadiness(p: Project, n: number): Readiness | null {
  const s3 = getS3(p);
  const c = p.collect;
  const m = p.master;
  const linkedCert = c ? mval(c.gp, 'eduCert') : '';
  if (n === 2) return chk([['GP 교육 수료증', !!s3.up.cert || !!linkedCert]]);
  if (n === 3) return chk([
    ['VICS ID·PW', !!(s3.vics.id && s3.vics.pw)],
    ['GP 교육 수료증', !!s3.up.cert || !!linkedCert],
    ['전문성 확인서 날인본', sealed(p, 'expert')],
    ['GP 동의서 날인본', sealed(p, 'privacy')],
    ['조합 주소지 등기부등본', !!s3.up.reg],
  ]);
  if (n === 4) return chk([
    ['승인 공문', !!getUp(p, 's4', 'approval')],
    ['GP 신분증(1단계)', !!(c && mval(c.gp, 'idCard'))],
  ]);
  if (n === 5) return chk([
    ['고유번호증', !!getUp(p, 's5', 'unique')],
    ['승인 공문(4단계 연동)', !!getUp(p, 's4', 'approval')],
    ['은행 구비서류 안내장', !!getUp(p, 's5', 'bankguide') || !!(p.master && p.master.gp && p.master.gp.name)],   // 자동 생성(2026-10-31) · 예전 업로드본도 인정
  ]);
  if (n === 6) return chk([
    ['신용정보조회서', !!getUp(p, 's6', 'credit')],
    ['출자내역 증빙', !!getUp(p, 's6', 'capproof')],
    ['잔고증명서', !!getUp(p, 's6', 'balance')],
  ]);
  if (n === 7) return chk([
    ['조합 고유번호', !!(m && m.uniqueNo)],
    ['행정정보 동의서 날인본', sealed(p, 'admininfo')],
    ['등록 신청서 날인본', sealed(p, 'regform')],
    ['의사록 날인본', sealed(p, 'minutes')],
    ['명부 날인본', sealed(p, 'roster')],
    ['출자증표 날인본', sealed(p, 'jeungpyo')],
    ['LP 동의서 날인본', sealed(p, 'lpprivacy')],
    ['잔고증명서(6단계)', !!getUp(p, 's6', 'balance')],
    ['출자이행 증빙(6단계)', !!getUp(p, 's6', 'capproof')],
    ['고유번호증(5단계)', !!getUp(p, 's5', 'unique')],
    ['신용정보조회서(6단계)', !!getUp(p, 's6', 'credit')],
    // 등록 서류 날짜(2026-10-22) — 총회 후 14일 안 · 모든 등록 서류가 결성총회일 날짜
    [regMissLabel(p), regOk(p)],
  ]);
  if (n >= 8 && n <= LAST) {
    if (!c) return null;
    const cos = c.cos;
    if (!cos.length) return { done: 0, total: 1, missing: ['투자법인 등록(1단계)'] };
    const list: [string, boolean][] = [];
    cos.forEach((co) => { coItems(p, co, n).forEach((x) => list.push(x)); });
    // 13단계: VICS [1329] 보고 검증·확정은 조합 단위로 한 번
    if (n === 13) list.push(['VICS 1329 보고 검증·확정 (조합)', vicsFundDone(p)]);
    return chk(list);
  }
  return null;
}
